import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, X, ImageOff, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const collections = [
  { id: 'all', name: 'All Fragrances', arabic: 'جميع العطور', desc: 'Browse our complete collection' },
  { id: 'best-sellers', name: 'Best Sellers', arabic: 'الأكثر مبيعاً', desc: 'Our most loved fragrances' },
  { id: 'gift-sets', name: 'Gift Sets', arabic: 'مجموعات الهدايا', desc: 'Curated sets for gifting' },
  { id: 'reef', name: 'Reef', arabic: 'ريف', desc: 'From the heart of Riyadh' },
  { id: 'gissah', name: 'Gissah', arabic: 'قصة', desc: 'The Kuwait legacy' },
  { id: 'ibraq', name: 'IBRAQ', arabic: 'إبراق', desc: 'Bold modern fragrances' },
  { id: 'paris-corner', name: 'Paris Corner', arabic: 'باريس كورنر', desc: 'Parisian gourmand essentials' },
  { id: 'emir', name: 'Emir', arabic: 'إمير', desc: 'Bold, sweet, oriental' },
  { id: 'afnan', name: 'Afnan', arabic: 'أفنان', desc: 'Modern Arabian icons' },
  { id: 'lattafa', name: 'Lattafa', arabic: 'لطافة', desc: 'The Khamrah dynasty' },
  { id: 'french-avenue', name: 'French Avenue', arabic: 'فرنش أفنيو', desc: 'Parisian gourmand luxury' },
  { id: 'rasasi', name: 'Rasasi', arabic: 'رصاصي', desc: 'Emirati powerhouse since 1979' },
  { id: 'ahmed-al-maghribi', name: 'Ahmed Al Maghribi', arabic: 'أحمد المغربي', desc: 'Modern Saudi artistry' },
  { id: 'arab', name: 'Arab', arabic: 'عرب', desc: 'Saudi elegance, bottled' },
  { id: 'arabiyat-prestige', name: 'Arabiyat Prestige', arabic: 'عربيات برستيج', desc: 'Sweet oriental gourmands' },
  { id: 'asdaaf', name: 'Asdaaf', arabic: 'أصداف', desc: 'Timeless Emirati florals' },
  { id: 'volare', name: 'Volare', arabic: 'فولاري', desc: 'Mediterranean escapes' },
  { id: 'grandeur-elite', name: 'Grandeur Elite', arabic: 'غراندور إليت', desc: 'Al Wataniah luxury line' },
  { id: 'al-rehab', name: 'Al Rehab', arabic: 'الرحاب', desc: 'Classic Saudi sweetness' },
  { id: 'rayhaan', name: 'Rayhaan', arabic: 'ريحان', desc: 'Tropical modern escapes' },
  { id: 'zimaya', name: 'Zimaya', arabic: 'زمايا', desc: 'Extrait de parfum florals' },
  { id: 'montera', name: 'Montera', arabic: 'مونتيرا', desc: 'Golden warmth, instant charm' },
  { id: 'joyous', name: 'Joyous', arabic: 'جويوس', desc: 'Sweetness that makes you smile' },
  { id: 'sale', name: 'Sale', arabic: 'تخفيضات', desc: 'Special offers' },
];

const COLLECTION_SLIDES = [
  {
    image: "https://cdn.salla.sa/PzAO/380a9a06-d21f-4238-9b44-e8c8bc6e1fbc-1000x1000-YIUQKnzf7ZuOPVccjIiT3L4ltD9UZvZQDhTBxioW.jpg",
    alt: "Lady Reef",
  },
  {
    image: "https://sa.gissah.com/web/image/product.template/1751/image_1920/1024x0",
    alt: "Imperial Valley by Gissah",
  },
  {
    image: "https://cdn.salla.sa/PzAO/c6138e7c-f153-4fe3-9493-72e7ed26cb90-1000x1000-GE19Owowl3rmTHtr7B7mOFNj9vrVNTa5sj34JRw9.jpg",
    alt: "Reef 11",
  },
  {
    image: "https://sa.gissah.com/web/image/product.template/1735/image_1920/1024x0",
    alt: "La Luna Valley by Gissah",
  },
  {
    image: "https://cdn.salla.sa/PzAO/ed842607-4399-452d-9c13-797ed6c97c30-1000x1000-fFedHeIQ9K17UYEUHMCIeCl1rRhkQx7noGrXs4wP.jpg",
    alt: "Bluest by Reef",
  },
];

const CollectionPicker = ({ onSelect }) => {
  const [fadeIn, setFadeIn] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isZooming, setIsZooming] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Preload slide images
  useEffect(() => {
    COLLECTION_SLIDES.forEach(({ image }) => {
      const img = new Image();
      img.src = image;
    });
  }, []);

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setIsZooming(false);
      setTimeout(() => {
        setSlideIndex(prev => (prev + 1) % COLLECTION_SLIDES.length);
        setIsZooming(true);
      }, 50);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 flex flex-col" data-testid="collection-picker">
      {/* Hero Slideshow */}
      <div className="relative h-[30vh] md:h-[35vh] overflow-hidden">
        {COLLECTION_SLIDES.map(({ image, alt }, i) => (
          <div
            key={image}
            className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: slideIndex === i ? 1 : 0 }}
          >
            <img
              src={image}
              alt={alt}
              className="w-full h-full object-cover"
              style={{
                transform: slideIndex === i && isZooming ? 'scale(1.12)' : 'scale(1.03)',
                transition: 'transform 6s ease-out',
              }}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-[#050505]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="absolute inset-0 arabian-pattern opacity-20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="font-arabic text-2xl text-[#D4AF37]/60 mb-2">مجموعتنا</p>
            <h1 className="font-heading text-4xl md:text-6xl tracking-wide text-white">
              OUR <span className="text-[#D4AF37]">FRAGRANCES</span>
            </h1>
          </div>
        </div>
        {/* Slide indicators */}
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 flex gap-1.5">
          {COLLECTION_SLIDES.map((slide, i) => (
            <button
              key={slide.image}
              onClick={() => { setSlideIndex(i); setIsZooming(true); }}
              data-testid={`collection-slide-indicator-${i}`}
              className={`h-[2px] transition-all duration-500 ${
                slideIndex === i
                  ? 'w-6 bg-[#D4AF37]'
                  : 'w-3 bg-white/25 hover:bg-white/40'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Collection Cards */}
      <div className="flex-1 flex items-center justify-center py-12 md:py-16 px-6">
        <div className={`max-w-3xl w-full transition-all duration-700 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-heading text-xs tracking-[0.3em] text-[#D4AF37]/60 text-center mb-8">CHOOSE A COLLECTION</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {collections.map((col, i) => (
              <button
                key={col.id}
                onClick={() => onSelect(col.id)}
                data-testid={`collection-pick-${col.id}`}
                className="group relative text-left p-5 md:p-6 border border-[#D4AF37]/15 bg-[#0a0908] hover:border-[#D4AF37]/50 hover:bg-[#0f0d0a] transition-all duration-300"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-arabic text-base text-[#D4AF37]/30 mb-0.5">{col.arabic}</p>
                    <h3 className="font-heading text-lg md:text-xl tracking-wide text-white group-hover:text-[#D4AF37] transition-colors">
                      {col.name.toUpperCase()}
                    </h3>
                    <p className="font-body text-white/30 text-xs mt-1">{col.desc}</p>
                  </div>
                  <ChevronRight size={20} className="text-[#D4AF37]/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </div>
                {col.id === 'sale' && (
                  <span className="absolute top-3 right-3 font-heading text-[8px] tracking-wider text-[#050505] bg-[#D4AF37] px-2 py-0.5">SALE</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  
  const activeCollection = searchParams.get('collection') || null;
  const searchQuery = searchParams.get('q') || '';

  // If no collection selected and no search query, show picker
  const showPicker = !activeCollection && !searchQuery;

  useEffect(() => {
    if (showPicker) {
      setLoading(false);
      return;
    }
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCollection && activeCollection !== 'all') params.set('collection', activeCollection);
        if (searchQuery) params.set('q', searchQuery);
        const url = `${API}/products${params.toString() ? '?' + params.toString() : ''}`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCollection, searchQuery, showPicker]);

  // Live stock sync — polls every 45s while the user is on the products page.
  // Updates SOLD OUT badges in real time without re-fetching the full catalog.
  useEffect(() => {
    if (showPicker) return undefined;
    let cancelled = false;
    const syncStock = async () => {
      try {
        const { data: stockMap } = await axios.get(`${API}/products/stock-map`);
        if (cancelled) return;
        setProducts((prev) => prev.map((p) => (
          stockMap[p.id] !== undefined && stockMap[p.id] !== p.in_stock
            ? { ...p, in_stock: stockMap[p.id] }
            : p
        )));
      } catch (e) { /* silently ignore — next poll retries */ }
    };
    const interval = setInterval(syncStock, 45000);
    // Also sync when tab becomes visible again
    const onVisibility = () => { if (!document.hidden) syncStock(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [showPicker]);

  const handleCollectionSelect = (collectionId) => {
    if (collectionId === 'all') {
      setSearchParams({ collection: 'all' });
    } else {
      setSearchParams({ collection: collectionId });
    }
  };

  const activeCol = collections.find(c => c.id === activeCollection);

  if (showPicker) {
    return <CollectionPicker onSelect={handleCollectionSelect} />;
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="products-page">
      {/* Slim header with back to collections */}
      <div className="bg-[#050505] border-b border-[#D4AF37]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8">
          <button
            onClick={() => setSearchParams({})}
            className="font-heading text-[10px] tracking-[0.2em] text-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors mb-3 flex items-center gap-1"
            data-testid="back-to-collections"
          >
            <ChevronRight size={12} className="rotate-180" />
            ALL COLLECTIONS
          </button>
          <div className="flex items-center justify-between">
            <div>
              {activeCol && <p className="font-arabic text-lg text-[#D4AF37]/40 mb-1">{activeCol.arabic}</p>}
              <h1 className="font-heading text-3xl md:text-4xl tracking-wide text-white" data-testid="products-page-title">
                {activeCol ? activeCol.name.toUpperCase() : searchQuery ? `SEARCH: "${searchQuery.toUpperCase()}"` : 'ALL FRAGRANCES'}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-body text-white/30 text-xs hidden md:block">
                {products.length} {products.length === 1 ? 'product' : 'products'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  data-testid="view-grid-button"
                  className={`p-2 border transition-colors duration-200 ${viewMode === 'grid' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-white/50'}`}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="view-list-button"
                  className={`p-2 border transition-colors duration-200 ${viewMode === 'list' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-white/50'}`}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Collection filter chips */}
      <div className="sticky top-20 md:top-24 z-30 bg-[#050505]/98 backdrop-blur-md border-b border-[#D4AF37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3">
          <div className="flex gap-3 overflow-x-auto hide-scrollbar">
            {collections.map((col) => (
              <button
                key={col.id}
                onClick={() => handleCollectionSelect(col.id)}
                data-testid={`filter-${col.id}`}
                className={`flex-shrink-0 px-4 py-2 font-heading text-[10px] tracking-[0.15em] border transition-all duration-200 ${
                  activeCollection === col.id
                    ? 'bg-[#D4AF37] text-[#050505] border-[#D4AF37]'
                    : 'text-white/50 border-[#D4AF37]/15 hover:border-[#D4AF37]/40 hover:text-white'
                }`}
              >
                {col.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-4 md:gap-6`}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-900/50 animate-pulse border border-[#D4AF37]/10" />
            ))}
          </div>
        ) : (
          <div
            className={`grid ${viewMode === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-4 md:gap-6`}
            data-testid="products-grid"
          >
            {products.map((product) => (
              <div key={product.id}>
                <Link to={`/products/${product.id}`} data-testid={`product-link-${product.id}`}>
                  {viewMode === 'grid' ? (
                    <div className="group border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-colors duration-300 bg-[#0a0a0a]">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#0d0d0d]">
                            <ImageOff size={40} className="text-[#D4AF37]/20 mb-3" />
                            <span className="font-heading text-[10px] tracking-[0.15em] text-[#D4AF37]/30">COMING SOON</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
                        <div className="absolute inset-0 bg-[#D4AF37]/0 group-hover:bg-[#D4AF37]/10 transition-colors duration-300" />
                        <div className="absolute top-3 left-3">
                          <span className="font-heading text-[9px] tracking-[0.15em] text-white/60 bg-[#050505]/70 px-2 py-1 backdrop-blur-sm border border-[#D4AF37]/15">
                            {product.brand?.toUpperCase()}
                          </span>
                        </div>
                        {product.discount_pct ? (
                          <div className="absolute top-3 left-3">
                            <span className="font-heading text-[9px] tracking-[0.15em] text-[#050505] bg-[#D4AF37] px-2 py-1">
                              {product.discount_pct}% OFF
                            </span>
                          </div>
                        ) : (product.collections?.includes('sale') && (
                          <div className="absolute top-3 right-3">
                            <span className="font-heading text-[9px] tracking-[0.15em] text-[#050505] bg-[#D4AF37] px-2 py-1">
                              SALE
                            </span>
                          </div>
                        ))}
                        {!product.in_stock && (
                          <div className="absolute inset-0 bg-[#050505]/60 flex items-center justify-center">
                            <span className="font-heading text-sm tracking-[0.2em] text-white/80 border border-white/30 px-4 py-2 backdrop-blur-sm">
                              SOLD OUT
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-3 md:p-5">
                        <h3 className="font-heading text-sm md:text-base tracking-wide text-white mb-1">
                          {product.name}
                        </h3>
                        <p className="font-body text-white/40 text-xs mb-2">
                          {product.subtitle} {product.size && `\u00B7 ${product.size}`}
                        </p>
                        <div className="flex items-baseline gap-2">
                          {product.original_price && product.original_price > product.price && (
                            <span className="font-body text-xs text-white/40 line-through">
                              A${product.original_price.toFixed(2)}
                            </span>
                          )}
                          {product.price > 0 && (
                            <span className="font-heading text-base md:text-lg text-[#D4AF37]">
                              A${product.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-6 md:gap-8 p-4 md:p-6 border border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-colors duration-300 bg-[#0a0a0a]">
                      <div className="w-24 h-32 md:w-32 md:h-40 flex-shrink-0 overflow-hidden">
                        {product.image ? (
                          <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#0d0d0d]">
                            <ImageOff size={24} className="text-[#D4AF37]/20" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-heading text-[10px] tracking-[0.15em] text-[#D4AF37]/60 border border-[#D4AF37]/20 px-2 py-0.5">
                            {product.brand?.toUpperCase()}
                          </span>
                          {product.discount_pct ? (
                            <span className="font-heading text-[10px] tracking-[0.15em] text-[#050505] bg-[#D4AF37] px-2 py-0.5">{product.discount_pct}% OFF</span>
                          ) : (product.collections?.includes('sale') && (
                            <span className="font-heading text-[10px] tracking-[0.15em] text-[#050505] bg-[#D4AF37] px-2 py-0.5">SALE</span>
                          ))}
                          {!product.in_stock && (
                            <span className="font-heading text-[10px] tracking-[0.15em] text-white/60 bg-red-900/50 border border-red-500/30 px-2 py-0.5">SOLD OUT</span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg md:text-xl tracking-wide text-white mb-1 truncate">{product.name}</h3>
                        <p className="font-body text-white/50 text-sm mb-2">{product.subtitle} {product.size && `\u00B7 ${product.size}`}</p>
                        {product.description && (
                          <p className="font-body text-white/40 text-sm mb-4 line-clamp-2 hidden md:block">{product.description}</p>
                        )}
                        <div className="flex items-baseline gap-2">
                          {product.original_price && product.original_price > product.price && (
                            <span className="font-body text-sm text-white/40 line-through">A${product.original_price.toFixed(2)}</span>
                          )}
                          {product.price > 0 && (
                            <span className="font-heading text-lg md:text-xl text-[#D4AF37]">A${product.price.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              </div>
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-20">
            <p className="font-body text-white/50">No fragrances found in this collection.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
